import type { EntityLink } from '@backstage/catalog-model';

export type EntityPageLinkBucket =
  | 'documentation'
  | 'howToUse'
  | 'useful'
  | 'other';

export type EntityPageLinkClassification =
  | 'apiReference'
  | 'examples'
  | 'runbook'
  | 'playbook'
  | 'dashboard'
  | 'logs'
  | 'pagerduty'
  | 'slo'
  | 'source'
  | 'apiExplorer'
  | 'documentation'
  | 'generic';

export type EntityPageLinkSource = 'catalog' | 'convention';

export type EntityPageClassifiedLink = EntityLink & {
  bucket: EntityPageLinkBucket;
  classification: EntityPageLinkClassification;
  source: EntityPageLinkSource;
  title: string;
};

export type EntityPageLinkGroups = {
  all: EntityPageClassifiedLink[];
  documentation: EntityPageClassifiedLink[];
  howToUse: EntityPageClassifiedLink[];
  useful: EntityPageClassifiedLink[];
  other: EntityPageClassifiedLink[];
};

const getNormalizedTitleText = (link: Pick<EntityLink, 'icon' | 'title'>) =>
  [link.title, link.icon].filter(Boolean).join(' ').toLocaleLowerCase('en-US');

const getNormalizedUrlText = (link: Pick<EntityLink, 'url'>) =>
  link.url.toLocaleLowerCase('en-US');

const getFallbackTitle = (url: string) => {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url.replace(/^\/+/, '') || 'Link';
  }
};

const classifyLink = ({
  icon,
  title,
  url,
}: Pick<EntityLink, 'icon' | 'title' | 'url'>): Pick<
  EntityPageClassifiedLink,
  'bucket' | 'classification'
> => {
  const normalizedTitle = getNormalizedTitleText({ icon, title });
  const normalizedUrl = getNormalizedUrlText({ url });

  if (
    normalizedTitle.includes('api explorer') ||
    normalizedTitle.includes('postman') ||
    normalizedTitle.includes('insomnia')
  ) {
    return { bucket: 'useful', classification: 'apiExplorer' };
  }

  if (
    normalizedTitle.includes('openapi') ||
    normalizedTitle.includes('swagger') ||
    normalizedTitle.includes('api reference') ||
    normalizedTitle.includes('definition')
  ) {
    return { bucket: 'howToUse', classification: 'apiReference' };
  }

  if (
    normalizedTitle.includes('example') ||
    normalizedTitle.includes('sample request') ||
    normalizedTitle.includes('sample response')
  ) {
    return { bucket: 'howToUse', classification: 'examples' };
  }

  if (normalizedTitle.includes('runbook')) {
    return { bucket: 'documentation', classification: 'runbook' };
  }

  if (
    normalizedTitle.includes('playbook') ||
    normalizedTitle.includes('on-call') ||
    normalizedTitle.includes('on call')
  ) {
    return { bucket: 'documentation', classification: 'playbook' };
  }

  if (
    normalizedTitle.includes('grafana') ||
    normalizedTitle.includes('dashboard') ||
    normalizedTitle.includes('metric') ||
    normalizedUrl.includes('grafana')
  ) {
    return { bucket: 'useful', classification: 'dashboard' };
  }

  if (
    normalizedTitle.includes('kibana') ||
    normalizedTitle.includes('log') ||
    normalizedUrl.includes('kibana')
  ) {
    return { bucket: 'useful', classification: 'logs' };
  }

  if (
    normalizedTitle.includes('pagerduty') ||
    normalizedTitle.includes('incident') ||
    normalizedUrl.includes('pagerduty')
  ) {
    return { bucket: 'useful', classification: 'pagerduty' };
  }

  if (
    normalizedTitle.includes('slo') ||
    normalizedTitle.includes('service level')
  ) {
    return { bucket: 'useful', classification: 'slo' };
  }

  let normalizedHost = '';
  try {
    normalizedHost = new URL(url).hostname.toLowerCase();
  } catch {
    normalizedHost = '';
  }

  const isGithubHost =
    normalizedHost === 'github.com' || normalizedHost.endsWith('.github.com');
  const isGitlabHost =
    normalizedHost === 'gitlab.com' || normalizedHost.endsWith('.gitlab.com');

  if (
    normalizedTitle.includes('github') ||
    normalizedTitle.includes('gitlab') ||
    normalizedTitle.includes('source') ||
    normalizedTitle.includes('repository') ||
    normalizedTitle.includes('repo') ||
    isGithubHost ||
    isGitlabHost
  ) {
    return { bucket: 'useful', classification: 'source' };
  }

  if (
    normalizedTitle.includes('docs') ||
    normalizedTitle.includes('documentation') ||
    normalizedTitle.includes('guide') ||
    normalizedTitle.includes('how-to') ||
    normalizedTitle.includes('how to') ||
    icon === 'docs'
  ) {
    return { bucket: 'documentation', classification: 'documentation' };
  }

  return { bucket: 'other', classification: 'generic' };
};

export const normalizeEntityLink = (
  link: EntityLink,
  source: EntityPageLinkSource = 'catalog',
): EntityPageClassifiedLink | undefined => {
  if (!link.url) {
    return undefined;
  }

  const classification = classifyLink(link);
  const title = link.title?.trim() || getFallbackTitle(link.url);

  return {
    ...link,
    ...classification,
    source,
    title,
  };
};

export const groupEntityLinks = (
  links: EntityLink[] | undefined,
): EntityPageLinkGroups => {
  const normalizedLinks = (links ?? [])
    .map(link => normalizeEntityLink(link))
    .filter((link): link is EntityPageClassifiedLink => Boolean(link));

  return {
    all: normalizedLinks,
    documentation: normalizedLinks.filter(
      link => link.bucket === 'documentation',
    ),
    howToUse: normalizedLinks.filter(link => link.bucket === 'howToUse'),
    useful: normalizedLinks.filter(link => link.bucket === 'useful'),
    other: normalizedLinks.filter(link => link.bucket === 'other'),
  };
};
